import React from "react";
import { useTranslation } from "react-i18next";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/outline";
import { PMDialog } from "../../../components/utils/PMDialog";
import { HseDocumentSelector } from "../../../components/utils/moduleSelectors/hseDocumentSelector";
import { RECURRENCE_OPTIONS } from "./todoConstants";
import { buildTemplateSummaryParts, recurrenceLabel } from "./todoTemplateSettingsUtils";

const inputClass =
  "block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500";

const labelClass = "mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500";

export const TodoTemplateEditDialog = ({
  open,
  onClose,
  template,
  templateIndex,
  categories,
  activeEmployeePositions,
  selectedEmployeesByRow,
  expanded,
  descriptionOpen,
  onToggleExpanded,
  onToggleDescription,
  onUpdate,
  onOpenAssignees,
  onApplyCategory,
  blockClose = false,
}) => {
  const { t } = useTranslation();

  if (!template) return null;

  const summaryParts = buildTemplateSummaryParts(template, {
    t,
    employeePositions: activeEmployeePositions,
    selectedEmployeesByRow,
  });

  return (
    <PMDialog
      open={open}
      onClose={onClose}
      title={template.title?.trim() || t("todo.settingsNewTemplate")}
      size="3xl"
      dense
      closeOnOutsideClick={!blockClose}
    >
      <div className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label className={labelClass}>{t("todo.createTitlePlaceholder")}</label>
            <input
              className={inputClass}
              value={template.title || ""}
              placeholder={t("todo.settingsTitlePlaceholder")}
              onChange={(e) => onUpdate(templateIndex, { title: e.target.value })}
            />
          </div>
          <div>
            <HseDocumentSelector
              showField
              fieldVariant="compact"
              name="task_reference_id"
              label="todo.settingsHseDocument"
              selected={
                template.task_reference_id
                  ? template.task_reference || {
                      id: template.task_reference_id,
                      displayTitle: `#${template.task_reference_id}`,
                    }
                  : null
              }
              onChange={(event) => {
                const doc = event?.target?.hse_document || event?.target?.data?.object;
                onUpdate(templateIndex, {
                  task_reference_id: event?.target?.value || null,
                  task_reference_type: event?.target?.value ? "Hse::Document" : "",
                  task_reference: doc
                    ? {
                        id: doc.id,
                        displayTitle: doc.title || doc.primary,
                        document_number: doc.full_document_number || doc.document_number,
                      }
                    : null,
                });
              }}
            />
          </div>
        </div>

        {!descriptionOpen ? (
          <button
            type="button"
            className="text-sm font-medium text-blue-600 hover:text-blue-700"
            onClick={() => onToggleDescription(templateIndex, true)}
          >
            + {t("todo.settingsAddDescription")}
          </button>
        ) : (
          <div>
            <label className={labelClass}>{t("todo.descriptionLabel")}</label>
            <textarea
              className={`${inputClass} min-h-[72px]`}
              value={template.description || ""}
              onChange={(e) => onUpdate(templateIndex, { description: e.target.value })}
            />
          </div>
        )}

        {!expanded && summaryParts.length ? (
          <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
              {t("todo.settingsCompactSummary")}
            </p>
            <p className="mt-1 text-sm text-gray-700">{summaryParts.join(" · ")}</p>
          </div>
        ) : null}

        <div className="flex flex-wrap items-center gap-2 border-t border-gray-100 pt-3">
          <label className="inline-flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={template.active !== false}
              onChange={(e) => onUpdate(templateIndex, { active: e.target.checked })}
            />
            {t("todo.settingsTemplateActive")}
          </label>
          <button
            type="button"
            className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-gray-600 hover:bg-gray-50"
            onClick={() => onToggleExpanded(templateIndex)}
          >
            {expanded ? (
              <>
                <ChevronUpIcon className="h-4 w-4" />
                {t("todo.settingsHideDetails")}
              </>
            ) : (
              <>
                <ChevronDownIcon className="h-4 w-4" />
                {t("todo.settingsShowDetails")}
              </>
            )}
          </button>
        </div>

        {expanded ? (
          <div className="grid gap-3 border-t border-gray-100 pt-4 sm:grid-cols-2">
            <div>
              <label className={labelClass}>{t("todo.settingsCategory")}</label>
              <input
                className={inputClass}
                value={template.category || ""}
                list="todo-template-category-options"
                onChange={(e) => onUpdate(templateIndex, { category: e.target.value })}
              />
            </div>
            <div>
              <label className={labelClass}>{t("todo.settingsCopyCategoryPositions")}</label>
              <select
                className={inputClass}
                defaultValue=""
                onChange={(e) => {
                  if (e.target.value) onApplyCategory(templateIndex, e.target.value);
                  e.target.value = "";
                }}
              >
                <option value="">{t("todo.settingsCopyCategoryPositionsHint")}</option>
                {categories.map((category) => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelClass}>{t("todo.recurrence")}</label>
              <select
                className={inputClass}
                value={template.recurrence || "none"}
                onChange={(e) => onUpdate(templateIndex, { recurrence: e.target.value })}
              >
                {RECURRENCE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {t(opt.labelKey)}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelClass}>{t("todo.settingsDueOffsetDays")}</label>
              <input
                type="number"
                min="0"
                className={inputClass}
                value={template.due_offset_days ?? 0}
                onChange={(e) =>
                  onUpdate(templateIndex, { due_offset_days: Number(e.target.value) || 0 })
                }
              />
            </div>
            <div className="sm:col-span-2">
              <label className={labelClass}>{t("todo.settingsWorkingPositions")}</label>
              {activeEmployeePositions.length ? (
                <div className="flex flex-wrap gap-2">
                  {activeEmployeePositions.map((position) => {
                    const selected = (template.target_employee_position_ids || []).includes(position.id);
                    return (
                      <button
                        key={position.id || position.name}
                        type="button"
                        disabled={!position.id}
                        className={`rounded-full border px-3 py-1 text-xs font-medium ${
                          selected
                            ? "border-blue-500 bg-blue-50 text-blue-700"
                            : "border-gray-300 bg-white text-gray-600 hover:border-gray-400"
                        } ${!position.id ? "opacity-60" : ""}`}
                        onClick={() => {
                          if (!position.id) return;
                          const current = template.target_employee_position_ids || [];
                          onUpdate(templateIndex, {
                            target_employee_position_ids: selected
                              ? current.filter((id) => id !== position.id)
                              : [...current, position.id],
                          });
                        }}
                      >
                        {position.name}
                      </button>
                    );
                  })}
                </div>
              ) : (
                <p className="text-sm text-gray-500">{t("todo.settingsNoPositionsHint")}</p>
              )}
            </div>
            <div className="sm:col-span-2">
              <label className={labelClass}>{t("todo.settingsAssignees")}</label>
              <div className="flex flex-wrap items-center gap-2">
                {(template.target_user_ids || []).map((userId) => (
                  <span
                    key={userId}
                    className="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-gray-50 px-3 py-1 text-xs font-medium text-gray-700"
                  >
                    {selectedEmployeesByRow[userId]?.name ||
                      selectedEmployeesByRow[userId]?.displayTitle ||
                      `#${userId}`}
                    <button
                      type="button"
                      className="text-gray-400 hover:text-gray-700"
                      onClick={() =>
                        onUpdate(templateIndex, {
                          target_user_ids: (template.target_user_ids || []).filter((id) => id !== userId),
                        })
                      }
                    >
                      ×
                    </button>
                  </span>
                ))}
                <button
                  type="button"
                  className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 shadow-sm hover:bg-gray-50"
                  onClick={() => onOpenAssignees(templateIndex)}
                >
                  {t("todo.settingsAddAssignees")}
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="grid gap-2 rounded-md border border-dashed border-gray-200 bg-gray-50/80 px-3 py-2 text-xs text-gray-600 sm:grid-cols-2">
            <span>
              <strong className="text-gray-500">{t("todo.settingsCategory")}:</strong>{" "}
              {template.category?.trim() || t("todo.settingsNotSet")}
            </span>
            <span>
              <strong className="text-gray-500">{t("todo.recurrence")}:</strong>{" "}
              {recurrenceLabel(template.recurrence, t) || t("todo.settingsNotSet")}
            </span>
            <span>
              <strong className="text-gray-500">{t("todo.settingsWorkingPositions")}:</strong>{" "}
              {(template.target_employee_position_ids || []).length
                ? (template.target_employee_position_ids || [])
                    .map((id) => activeEmployeePositions.find((pos) => pos.id === id)?.name)
                    .filter(Boolean)
                    .join(", ")
                : t("todo.settingsNotSet")}
            </span>
            <span>
              <strong className="text-gray-500">{t("todo.settingsAssignees")}:</strong>{" "}
              {(template.target_user_ids || []).length
                ? t("todo.settingsAssigneeCount", { count: template.target_user_ids.length })
                : t("todo.settingsNotSet")}
            </span>
          </div>
        )}
      </div>
    </PMDialog>
  );
};

export default TodoTemplateEditDialog;
